/**
 * Collections Context
 * 
 * Provides global state management for user collections.
 * Handles fetching, creating, updating, and managing collections.
 * 
 * @author OriginSelect Team
 * @created December 1, 2025
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUser } from './UserContext';
import { getStoredGoogleIdToken, hasStoredGoogleIdToken } from '../utils/googleAuth';
import {
  fetchUserCollections,
  fetchCollection,
  createCollection as apiCreateCollection,
  updateCollection as apiUpdateCollection,
  deleteCollection as apiDeleteCollection,
  addProductToCollection as apiAddProduct,
  removeProductFromCollection as apiRemoveProduct,
  reorderCollectionItems as apiReorderItems,
  copyCollectionUrl
} from '../api/collectionsApi';

const CollectionsContext = createContext(null);

export const CollectionsProvider = ({ children }) => {
  const { user } = useUser();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [hasToken, setHasToken] = useState(false);

  // Monitor for Google token in localStorage (indicates authentication complete)
  useEffect(() => {
    const checkToken = () => {
      setHasToken(hasStoredGoogleIdToken());
    };
    
    // Check immediately
    checkToken();
    
    // Listen for storage changes
    window.addEventListener('storage', checkToken);
    
    // Poll every 500ms in case token is set in same tab (storage event doesn't fire for same-tab changes)
    const interval = setInterval(checkToken, 500);
    
    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, []);

  // Fetch all user collections
  const loadCollections = useCallback(async (market = null) => {
    if (!user?.email) {
      setCollections([]);
      return;
    }

    // Don't check for token here - the API interceptor will add it if available
    // If token is missing, the 401 error handler will try to recover it from Google
    setLoading(true);
    setError(null);
    
    try {
      console.log('📚 CollectionsContext.loadCollections: Fetching with market:', market, 'user:', user.email);
      const response = await fetchUserCollections(user.email, market);
      console.log('📚 loadCollections response:', JSON.stringify(response, null, 2));
      console.log('📚 Collections count:', response.collections?.length || 0);
      setCollections(response.collections || []);
    } catch (err) {
      console.error('Failed to load collections:', err);
      setError(err.message || 'Failed to load collections');
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  // Load collections when user changes or token becomes available
  useEffect(() => {
    if (user?.email && hasToken) {
      // Don't auto-load - let Collections page request with market parameter
    } else {
      setCollections([]);
      setSelectedCollection(null);
    }
  }, [user?.email, hasToken]);

  // Fetch a single collection with full details
  const getCollection = useCallback(async (collectionId) => {
    if (!user?.email || !collectionId) return null;

    try {
      const response = await fetchCollection(collectionId, user.email);
      return response.collection;
    } catch (err) {
      console.error('Failed to fetch collection:', err);
      throw err;
    }
  }, [user?.email]);

  // Create a new collection
  const createCollection = useCallback(async (title, description = '', options = {}) => {
    if (!user?.email) {
      throw new Error('Must be logged in to create a collection');
    }

    // Check if Google ID token is available
    const googleIdToken = typeof window !== 'undefined' ? getStoredGoogleIdToken() : null;
    if (!googleIdToken) {
      throw new Error('Authentication required. Please sign in with Google to create collections.');
    }

    try {
      const response = await apiCreateCollection({
        userId: user.email,
        title,
        description,
        isPublic: options.isPublic ?? true,
        coverImageUrl: options.coverImageUrl,
        initialProductIds: options.initialProductIds || [],
        market: options.market || 'canada' // 'canada' or 'global'
      });

      const newCollection = response.collection;
      setCollections(prev => [newCollection, ...prev]);
      
      return newCollection;
    } catch (err) {
      console.error('Failed to create collection:', err);
      throw err;
    }
  }, [user?.email]);

  // Update collection metadata
  const updateCollection = useCallback(async (collectionId, updates) => {
    if (!user?.email) {
      throw new Error('Must be logged in to update a collection');
    }

    try {
      const response = await apiUpdateCollection(collectionId, {
        userId: user.email,
        ...updates
      });

      const updatedCollection = response.collection;
      setCollections(prev => 
        prev.map(c => c.id === collectionId ? { ...c, ...updatedCollection } : c)
      );
      
      return updatedCollection;
    } catch (err) {
      console.error('Failed to update collection:', err);
      throw err;
    }
  }, [user?.email]);

  // Delete a collection
  const deleteCollection = useCallback(async (collectionId) => {
    if (!user?.email) {
      throw new Error('Must be logged in to delete a collection');
    }

    try {
      await apiDeleteCollection(collectionId, user.email);
      setCollections(prev => prev.filter(c => c.id !== collectionId));
      
      if (selectedCollection?.id === collectionId) {
        setSelectedCollection(null);
      }
      
      return true;
    } catch (err) {
      console.error('Failed to delete collection:', err);
      throw err;
    }
  }, [user?.email, selectedCollection?.id]);

  // Add a product to a collection
  const addToCollection = useCallback(async (collectionId, productId, note = '') => {
    if (!user?.email) {
      throw new Error('Must be logged in to add to a collection');
    }

    try {
      await apiAddProduct(collectionId, {
        userId: user.email,
        productId,
        note
      });

      // Update local state - increment product count
      setCollections(prev =>
        prev.map(c => c.id === collectionId 
          ? { ...c, productCount: (c.productCount || 0) + 1 }
          : c
        )
      );

      return true;
    } catch (err) {
      console.error('Failed to add to collection:', err);
      throw err;
    }
  }, [user?.email]);

  // Remove a product from a collection
  const removeFromCollection = useCallback(async (collectionId, productId) => {
    if (!user?.email) {
      throw new Error('Must be logged in to remove from a collection');
    }

    try {
      await apiRemoveProduct(collectionId, productId, user.email);

      // Update local state - decrement product count
      setCollections(prev =>
        prev.map(c => c.id === collectionId 
          ? { ...c, productCount: Math.max((c.productCount || 1) - 1, 0) }
          : c
        )
      );

      return true;
    } catch (err) {
      console.error('Failed to remove from collection:', err);
      throw err;
    }
  }, [user?.email]);

  // Reorder items in a collection
  const reorderItems = useCallback(async (collectionId, itemOrder) => {
    if (!user?.email) {
      throw new Error('Must be logged in to reorder items');
    }

    try {
      await apiReorderItems(collectionId, {
        userId: user.email,
        itemOrder
      });

      return true;
    } catch (err) {
      console.error('Failed to reorder items:', err);
      throw err;
    }
  }, [user?.email]);

  // Share a collection (copy URL)
  const shareCollection = useCallback(async (collection) => {
    const success = await copyCollectionUrl(collection);
    return success;
  }, []);

  // Modal controls for "Add to Collection"
  const openAddToCollectionModal = useCallback((product) => {
    setPendingProduct(product);
    setIsModalOpen(true);
  }, []);

  const closeAddToCollectionModal = useCallback(() => {
    setPendingProduct(null);
    setIsModalOpen(false);
  }, []);

  // Check if a product is in a collection
  const isProductInCollection = useCallback((collectionId, productId) => {
    const collection = collections.find(c => c.id === collectionId);
    if (!collection) {
      console.log(`isProductInCollection: collection ${collectionId} not found`);
      return false;
    }
    
    console.log(`isProductInCollection checking collection "${collection.title}":`, {
      collectionId,
      productId,
      hasProducts: !!collection.products,
      productsLength: collection.products?.length,
      hasProductIds: !!collection.productIds,
      productIdsLength: collection.productIds?.length,
      productCount: collection.productCount,
      rawCollection: collection
    });
    
    // Handle different data structures from API:
    // 1. products array with objects that have id or _id
    if (collection.products && Array.isArray(collection.products) && collection.products.length > 0) {
      const found = collection.products.some(p => {
        // Product could be a string ID or an object
        if (typeof p === 'string') {
          return p === productId;
        }
        return p.id === productId || p._id === productId || p.productId === productId;
      });
      console.log(`  → products array check: ${found}`);
      return found;
    }
    
    // 2. productIds array (array of string IDs)
    if (collection.productIds && Array.isArray(collection.productIds) && collection.productIds.length > 0) {
      const found = collection.productIds.includes(productId);
      console.log(`  → productIds array check: ${found}`);
      return found;
    }
    
    // 3. items array (alternative naming)
    if (collection.items && Array.isArray(collection.items) && collection.items.length > 0) {
      const found = collection.items.some(item => {
        if (typeof item === 'string') {
          return item === productId;
        }
        return item.id === productId || item._id === productId || item.productId === productId;
      });
      console.log(`  → items array check: ${found}`);
      return found;
    }
    
    console.log(`  → No products/productIds/items array found, returning false`);
    return false;
  }, [collections]);

  const value = {
    // State
    collections,
    loading,
    error,
    selectedCollection,
    isModalOpen,
    pendingProduct,

    // Actions
    loadCollections,
    getCollection,
    createCollection,
    updateCollection,
    deleteCollection,
    addToCollection,
    removeFromCollection,
    reorderItems,
    shareCollection,

    // Modal controls
    openAddToCollectionModal,
    closeAddToCollectionModal,
    setSelectedCollection,

    // Helpers
    isProductInCollection,
    
    // User state
    isLoggedIn: !!user?.email
  };

  return (
    <CollectionsContext.Provider value={value}>
      {children}
    </CollectionsContext.Provider>
  );
};

// Custom hook to use collections context
export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
};

export default CollectionsContext;
